/** @format */
import { MediaQuery } from '@mpb-com/feather/helpers/media-queries';
import { css } from 'styled-components';

import { ConstrainedWrapperType } from './constants';
import {
    getGridColumn,
    getGridTemplateColumns,
    getGutterSize,
    getMaxWidthSize,
    getPadding,
} from './style-helpers';
import { StyledConstrainedWrapperProps } from './types';

const baseStyles = css`
    margin: 0 auto;
`;

/**
 * Type styles for the constrained wrapper component
 *
 * @param {StyledConstrainedWrapperProps} props The constrained wrapper properties object
 * @param {ConstrainedWrapperType} props.type The constrained wrapper type
 * @param {ConstrainedWrapperSize} props.size The constrained wrapper size
 * @param {MediaQuery} props.fullWidth Media query at which we add styles to display full width
 * @param {ConstrainedWrapperGutter} props.gutter The gutter size
 * @param {ReactNode} props.children The children rendered in the wrapper
 * */
const typeStyles = ({
    type,
    size,
    fullWidth,
    gutter,
}: StyledConstrainedWrapperProps) => {
    switch (type) {
        case ConstrainedWrapperType.GRID: {
            return css`
                display: grid;

                > * {
                    grid-column: ${getGridColumn(size)};
                }

                ${[
                    MediaQuery.DESKTOP,
                    MediaQuery.LARGE_DESKTOP,
                    MediaQuery.LARGE_TABLET,
                    MediaQuery.SMALL_TABLET,
                    MediaQuery.MOBILE,
                    MediaQuery.SMALL_MOBILE,
                ].map(
                    (mediaQuery) => css`
                        @media ${mediaQuery} {
                            grid-template-columns: ${getGridTemplateColumns(
                                mediaQuery,
                                size,
                                gutter
                            )};
                        }
                    `
                )}

                // Order is important. We need this below the mobile media query
                // If fullWidth media query supplied, we want this to always trump the mobile rule
                @media only screen and ${fullWidth} {
                    grid-template-columns: 0 minmax(0, 1fr) 0;

                    > * {
                        grid-column: 2;
                    }
                }
            `;
        }
        case ConstrainedWrapperType.PADDING:
        default:
            return css`
                ${[
                    MediaQuery.DESKTOP,
                    MediaQuery.LARGE_DESKTOP,
                    MediaQuery.LARGE_TABLET,
                    MediaQuery.SMALL_TABLET,
                    MediaQuery.MOBILE,
                    MediaQuery.SMALL_MOBILE,
                ].map(
                    (mediaQuery) => css`
                        @media ${mediaQuery} {
                            max-width: calc(
                                ${getMaxWidthSize(size)} +
                                    (${getGutterSize(mediaQuery, gutter)} * 2)
                            );
                            padding: ${getPadding(mediaQuery, gutter)};
                        }
                    `
                )}

                // Order is important. We need this below the mobile media query
                // If fullWidth media query supplied, we want this to always trump the mobile rule
                @media only screen and ${fullWidth} {
                    padding: 0;
                    max-width: 100%;
                }
            `;
    }
};

export const styles = (props: StyledConstrainedWrapperProps) => css`
    ${baseStyles}
    ${typeStyles(props)}
`;
